import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Button } from "components/common";
import Clickable from "../common/Clickable";
import UnderlineInput from "../common/UnderlineInput";
import HsbcConfigurationSection from './HsbcConfigurationSection';
import { getHsbcKeys, uploadHsbcKey } from "../common/settingsApi";
import { SettingsCard } from "./settingsKit";

const KEYS = [
  {
    value: "hsbc_public_key",
    label: "HSBC public key",
    hint: "The public key HSBC publishes. Used to encrypt the payment files sent to HSBC.",
    accept: ".asc,.txt,.gpg,.pgp",
  },
  {
    value: "pebsteel_private_key",
    label: "Pebsteel private key",
    hint: "Pebsteel's private key. Used to sign the payment files, proving they come from Pebsteel.",
    accept: ".asc,.txt,.gpg,.pgp",
  },
];

const Label = ({ children }) => (
  <div className="tw-text-[12px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3 tw-mb-2">
    {children}
  </div>
);

Label.propTypes = {
  children: PropTypes.node,
};

const Fingerprint = ({ value }) => (
  <span className="tw-font-mono tw-text-[12px] tw-tabular-nums tw-text-geist-fg tw-break-all">
    {value || "—"}
  </span>
);

Fingerprint.propTypes = {
  value: PropTypes.string,
};

const KeyRow = ({ title, info }) => (
  <div className="tw-py-2.5">
    <div className="tw-flex tw-items-baseline tw-gap-2 tw-flex-wrap">
      <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg-2">{title}</span>
      <Fingerprint value={info?.fingerprint} />
    </div>
    {info && (
      <div className="tw-mt-1 tw-text-[12px] tw-text-geist-fg-3">
        {(info.userIds || info.user_ids || []).toString()}
        {info.expiresAt && <> · expires {String(info.expiresAt).slice(0, 10)}</>}
        {typeof info.daysUntilExpiry === "number" && info.daysUntilExpiry <= 60 && (
          <span className="tw-ml-1 tw-font-semibold tw-text-[#a05e03]">
            {info.daysUntilExpiry} days left
          </span>
        )}
      </div>
    )}
  </div>
);

KeyRow.propTypes = {
  title: PropTypes.string,
  // Key metadata from the gateway; camelCase or snake_case depending on the endpoint.
  info: PropTypes.shape({
    fingerprint: PropTypes.string,
    userIds: PropTypes.arrayOf(PropTypes.string),
    user_ids: PropTypes.arrayOf(PropTypes.string),
    expiresAt: PropTypes.string,
    daysUntilExpiry: PropTypes.number,
  }),
};

/**
 * PGP key administration for the HSBC gateway.
 *
 * Two things the UI must state plainly, because both are easy to misread:
 *
 * 1. The Portal does NOT store keys. It passes the file straight to the gateway.
 * 2. An uploaded key does NOT take effect immediately — the gateway must restart, so
 *    a payment file half-built with the old key is never signed with the new one.
 *
 * The fingerprint is required on purpose: it replaces pinning fingerprints in source.
 * The uploader must know the real fingerprint from an outside source (the issuer's
 * published one), so admin rights alone are not enough to swap a key.
 */
const PgpKeysSection = () => {
  const [state, setState] = useState({ loading: true, data: null, history: [], error: null });
  const [keyName, setKeyName] = useState(KEYS[0].value);
  const [fileName, setFileName] = useState("");
  const [armored, setArmored] = useState("");
  const [fingerprint, setFingerprint] = useState("");
  const [busy, setBusy] = useState(false);
  // The upload is only for REPLACING a key on the gateway (rotation without SSH) —
  // hidden until asked for, so it does not read as a required setup step.
  const [replacing, setReplacing] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await getHsbcKeys();
      setState({ loading: false, data: res.data, history: res.history || [], error: null });
    } catch (e) {
      setState({
        loading: false,
        data: null,
        history: [],
        error: e?.response?.data?.error || "Could not read the key status.",
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pickFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      if (!text.includes("BEGIN PGP")) {
        toast.warning("This file is not an ASCII-armored PGP key.");
        return;
      }
      setArmored(text);
      setFileName(file.name);
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const submit = async () => {
    if (!armored) return toast.warning("Choose a key file first.");
    if (!fingerprint.trim()) {
      return toast.warning("Enter the key fingerprint — check it against the one the issuer published.");
    }

    setBusy(true);
    try {
      const res = await uploadHsbcKey({
        key: keyName,
        armored,
        expectedFingerprint: fingerprint.trim(),
      });
      toast.success(res.message || "Key saved.");
      setArmored("");
      setFileName("");
      setFingerprint("");
      await load();
    } catch (e) {
      // The rejection reason (especially a fingerprint mismatch) is something the user
      // NEEDS to read verbatim — never shorten it to "something went wrong".
      const message = e?.response?.data?.error || "Could not save the key.";
      toast.error(message, { autoClose: 12000, style: { whiteSpace: "pre-line" } });
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <div className="tw-text-[13px] tw-text-geist-fg-3">Reading key status…</div>;
  }

  if (state.error) {
    return (
      <div>
        <div className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-px-4 tw-py-3 tw-text-[13px] tw-text-[#a05e03]">
          {state.error}
        </div>
      </div>
    );
  }

  const active = state.data?.active || {};
  const staged = state.data?.staged || {};
  const restartRequired = state.data?.restartRequired;
  const selected = KEYS.find((k) => k.value === keyName);

  return (
    <div className="tw-space-y-6">
      {restartRequired && (
        <div className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-px-4 tw-py-3">
          <div className="tw-text-[13px] tw-font-semibold tw-text-[#a05e03]">
            A new key is waiting — restart the HSBC gateway
          </div>
          <div className="tw-mt-1 tw-text-[12px] tw-text-[#a05e03]">
            The key is saved but not in use yet. The gateway keeps running on the old key until
            it restarts.
          </div>
        </div>
      )}

      <div>
        <Label>Active keys</Label>
        <div className="tw-divide-y tw-divide-geist-border tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-px-4">
          {KEYS.map((k) => (
            <KeyRow key={k.value} title={k.label} info={active[k.value]} />
          ))}
        </div>
      </div>

      {(staged.hsbc_public_key || staged.pebsteel_private_key) && (
        <div>
          <Label>Keys waiting for restart</Label>
          <div className="tw-divide-y tw-divide-geist-border tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-px-4">
            {KEYS.map((k) =>
              staged[k.value] ? (
                <div key={k.value}>
                  <KeyRow title={k.label} info={staged[k.value]} />
                  {staged[k.value]?.fingerprint && staged[k.value].fingerprint === active[k.value]?.fingerprint && (
                    <div className="tw-pb-2 tw-text-[12px] tw-text-[#067647]">Same as the active key — no restart needed for this one.</div>
                  )}
                </div>
              ) : null,
            )}
          </div>
        </div>
      )}

      <div className="tw-pt-5 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
        <Label>Replace a key on the gateway</Label>

        <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mb-4">
          Nothing to upload if the "Active keys" above are correct — the gateway already has them. Use this
          only to replace a key (HSBC issues a new key, the Pebsteel key is about to expire) without going onto
          the server. The Portal does not store keys — the file goes straight to the gateway; the new key takes
          effect after the gateway restarts.
        </p>
        {!replacing && (
          <Button variant="secondary" size="sm" onClick={() => setReplacing(true)}>Replace key…</Button>
        )}
        {replacing && (<>

        <div className="tw-flex tw-gap-2 tw-mb-4 tw-flex-wrap">
          {KEYS.map((k) => (
            <Clickable
              key={k.value}
              onClick={() => setKeyName(k.value)}
              className={`tw-h-9 tw-inline-flex tw-items-center tw-px-3.5 tw-rounded-geist-sm tw-text-[13px] tw-font-semibold tw-border tw-border-solid ${
                keyName === k.value
                  ? "tw-bg-geist-accent tw-text-white tw-border-geist-accent"
                  : "tw-bg-white tw-text-geist-fg-2 tw-border-geist-border hover:tw-bg-geist-bg-2"
              }`}
            >
              {k.label}
            </Clickable>
          ))}
        </div>

        <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mb-4">{selected?.hint}</p>

        <div className="tw-space-y-4">
          <div>
            <div className="tw-text-[12px] tw-font-semibold tw-text-geist-fg-2 tw-mb-1">
              Key file <span className="tw-text-geist-error">*</span>
            </div>
            <label className="tw-inline-flex tw-items-center tw-gap-2 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100 tw-cursor-pointer">
              <i className="bi bi-upload" />
              {fileName || "Choose file…"}
              <input type="file" accept={selected?.accept} onChange={pickFile} className="tw-hidden" />
            </label>
          </div>

          <div>
            <div className="tw-text-[12px] tw-font-semibold tw-text-geist-fg-2 tw-mb-1">
              Fingerprint <span className="tw-text-geist-error">*</span>
            </div>
            <UnderlineInput
              value={fingerprint}
              onChange={(e) => setFingerprint(e.target.value)}
              placeholder="8213 681B 84C4 CDCC 73C8  EB13 6FBA BF98 D743 826B"
            />
            <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-1.5">
              Check it against the fingerprint the key issuer published — never copy it from the file
              itself. The gateway refuses a file that does not match; that is how a swapped file is caught.
            </p>
          </div>

          <div className="tw-flex tw-justify-end">
            <Button variant="primary" size="md" disabled={busy} onClick={submit}>
              {busy ? "Saving…" : "Save key"}
            </Button>
          </div>
        </div>
        </>)}
      </div>

      {state.history.length > 0 && (
        <div className="tw-pt-5 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
          <Label>Key change history</Label>
          <div className="tw-divide-y tw-divide-geist-border">
            {state.history.map((h, i) => (
              <div key={`${h.changed_at}-${i}`} className="tw-py-2">
                <div className="tw-flex tw-items-baseline tw-gap-2 tw-flex-wrap">
                  <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg-2">
                    {h.key_name}
                  </span>
                  <Fingerprint value={h.fingerprint} />
                </div>
                <div className="tw-text-[12px] tw-text-geist-fg-3 tw-mt-0.5">
                  {h.changed_by || "—"}
                  {h.changed_at ? ` · ${h.changed_at}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Payment Request → Settings → HSBC. One form, one save for the connection (mode,
 * gateway URL, gateway API key, gateway admin key), Pebsteel
 * as payer and the per-payment defaults; below it, the gateway's PGP keys — read and
 * replaced through gpg-encrypt /api/admin/keys with the gateway admin key.
 */
const HsbcKeysPanel = () => {
  // Saving the form (e.g. a new admin key) reloads the PGP-key section.
  const [configurationVersion, setConfigurationVersion] = useState(0);
  return (
    <div className="tw-p-6 tw-space-y-6">
      <HsbcConfigurationSection onSaved={() => setConfigurationVersion((value) => value + 1)} />
      <SettingsCard title="Gateway PGP keys"
        subtitle="HSBC public key (encrypts) and Pebsteel private key (signs). Needs the gateway admin key above.">
        <div className="tw-p-5">
          <PgpKeysSection key={configurationVersion} />
        </div>
      </SettingsCard>
    </div>
  );
};

export default HsbcKeysPanel;
